import { Notification } from '@wix/design-system'
import React, { FC, PropsWithChildren, useCallback, useMemo, useState } from 'react'
import { createPortal } from 'react-dom'

import { errorNotificationDataHooks as dataHooks } from './ErrorNotification.dataHooks'
import { ErrorNotificationContext } from './ErrorNotificationContext'
import styles from './ErrorNotificationProvider.scss'

// Hardcoded English: the viewer has no client-side i18n — all other user-facing
// strings arrive as server page content, which is unavailable when a request fails.
const ERROR_NOTIFICATION_TITLE = "Site couldn't be created."
const ERROR_NOTIFICATION_DESCRIPTION_FIRST_LINE = "There's a temporary issue."
const ERROR_NOTIFICATION_DESCRIPTION_SECOND_LINE = 'Try again later.'
const ERROR_NOTIFICATION_DESCRIPTION = `${ERROR_NOTIFICATION_DESCRIPTION_FIRST_LINE} ${ERROR_NOTIFICATION_DESCRIPTION_SECOND_LINE}`
export const ERROR_NOTIFICATION_MESSAGE = `${ERROR_NOTIFICATION_TITLE} ${ERROR_NOTIFICATION_DESCRIPTION}`

export const ErrorNotificationProvider: FC<PropsWithChildren> = ({ children }) => {
  // 0 = hidden; each showError call gets a new id so the Notification remounts fresh
  // (its internal closed-by-user state is not resettable via props) and so that the
  // delayed onClose of a previously closed notification cannot hide a newer one
  const [showId, setShowId] = useState(0)
  const [outlet, setOutlet] = useState<HTMLElement | null>(null)

  const showError = useCallback(() => setShowId(id => id + 1), [])
  const hideError = useCallback(() => setShowId(0), [])
  const registerOutlet = useCallback((nextOutlet: HTMLElement | null) => {
    setOutlet(nextOutlet)

    return () => {
      setOutlet(currentOutlet => (currentOutlet === nextOutlet ? null : currentOutlet))
    }
  }, [])

  const value = useMemo(() => ({ showError, hideError, registerOutlet }), [showError, hideError, registerOutlet])

  // Mounted conditionally: the Notification `show` prop only handles false -> true —
  // it never removes the content once shown, so unmounting is the reliable way to hide.
  const notification =
    showId > 0 ? (
      <Notification
        key={showId}
        dataHook={dataHooks.notification()}
        show
        type={outlet ? 'global' : 'sticky'}
        skin="error"
        onClose={() => setShowId(current => (current === showId ? 0 : current))}
      >
        <Notification.TextLabel ellipsis={false}>
          <span className={styles.message}>
            <span className={styles.title}>{ERROR_NOTIFICATION_TITLE}</span>{' '}
            <span className={styles.description}>
              <span>{ERROR_NOTIFICATION_DESCRIPTION_FIRST_LINE}</span>{' '}
              <span>{ERROR_NOTIFICATION_DESCRIPTION_SECOND_LINE}</span>
            </span>
          </span>
        </Notification.TextLabel>
        <Notification.CloseButton />
      </Notification>
    ) : null

  return (
    <ErrorNotificationContext.Provider value={value}>
      {children}
      {outlet && notification ? createPortal(notification, outlet) : notification}
    </ErrorNotificationContext.Provider>
  )
}
